/**
 * @file comment model 评论相关数据
 */
import nanoid from 'nanoid';
import { BaseModel, apolloQuery, QueryResult, apolloMutation, MutationResult } from 'vue-apollo-model';
import gql from 'graphql-tag';
import { subCommentFragmentContent, commentFragmentContent } from './commentFragment';
import { Query, VisionSubCommentItem, VisionRootCommentItem, VisionAddCommentResult, Mutation } from '@/generated-models';

const BLOCKED_CODE = [ 135010 ];

export default class CommentModel extends BaseModel {
    comment = {
        fetchCursorValue: '',
        status: {
            sending: 'sending',
            success: 'success',
            fail: 'fail',
            done: 'done',
        },
    };

    commentEditState = {
        // 评论内容
        commentContent: '',
        // 回复的评论id
        replyToCommentId: '',
        // 回复的userid
        replyTo: '',
        // 回复的username
        replyToUserName: '',
    };

    // 回复的评论框
    replyCommentEditState = {
        // 评论内容
        commentContent: '',
        // 回复的评论id
        replyToCommentId: '',
        // 回复的userid
        replyTo: '',
        // 回复的username
        replyToUserName: '',
    };
    // 用于标记 当前使用的input
    replyInputId = '';
    // 当前作品id
    photoId = '';

    @apolloQuery<CommentModel>({
        query: gql`
            query commentListQuery($photoId: String, $pcursor: String) {
                visionCommentList(photoId: $photoId, pcursor: $pcursor) {
                    commentCount
                    pcursor
                    rootComments {
                        ${commentFragmentContent}
                    }
                }
            }
        `,
        variables($route) {
            return {
                photoId: $route.params.photoId || this.photoId,
                pcursor: '',
            };
        },
        fetchPolicy: 'cache-and-network',
        // 无photoid时不发送请求
        skip($route) {
            return !$route.params.photoId && !this.photoId;
        },
    })
    commentListQuery!: QueryResult<{
        visionCommentList: Query['visionCommentList'];
    }>

    @apolloQuery({
        query: gql`
            query visionBaseEmoticons{
                visionBaseEmoticons{
                    iconUrls
                }
            }
        `,
        fetchPolicy: 'cache-first',
    })
    emoticonsQuery!: QueryResult<{
        visionBaseEmoticons: Query['visionBaseEmoticons'];
    }>

    get emojiIconUrls() {
        return this.emoticonsQuery.data.visionBaseEmoticons?.iconUrls || {};
    }

    @apolloMutation<CommentModel>({
        mutation: gql`
            mutation visionSubCommentList($photoId: String, $rootCommentId: String, $pcursor: String) {
                visionSubCommentList(photoId: $photoId, rootCommentId: $rootCommentId, pcursor: $pcursor) {
                    pcursor
                    subComments {
                        ${subCommentFragmentContent}
                    }
                }
            }
        `,
        variables({ photoId, rootCommentId, pcursor }) {
            return {
                photoId,
                rootCommentId,
                pcursor,
            };
        },
    })
    private visionSubCommentListMutation!: MutationResult<
        { photoId: string; rootCommentId: string; pcursor: string },
        {
            visionSubCommentList: Mutation['visionSubCommentList'];
        }
    >

    get subCommentList() {
        return this.visionSubCommentListMutation.data.visionSubCommentList;
    }

    @apolloMutation<CommentModel>({
        mutation: gql`
            mutation visionAddComment(
                $photoId: String,
                $photoAuthorId: String,
                $content: String,
                $replyToCommentId: ID,
                $replyTo: ID,
                $expTag: String) {
                visionAddComment(
                    photoId: $photoId,
                    photoAuthorId: $photoAuthorId,
                    content: $content,
                    replyToCommentId: $replyToCommentId,
                    replyTo: $replyTo,
                    expTag: $expTag) {
                    result
                    commentId
                    content
                    timestamp
                    status
                }
            }
        `,
        variables({ photoId, photoAuthorId, content, replyToCommentId, replyTo, expTag }) {
            return {
                photoId,
                photoAuthorId,
                content,
                replyToCommentId,
                replyTo,
                expTag,
            };
        },
    })
    private addNewCommentMutation!: MutationResult<
        { photoId: string; photoAuthorId: string; content: string; replyToCommentId: number; replyTo: string; expTag: string },
        {
            visionAddComment: Mutation['visionAddComment'];
        }
    >;

    async addNewComment({
        photoId,
        photoAuthorId,
        content,
        replyToCommentId,
        replyTo,
        expTag,
    }: { photoId: string; photoAuthorId: string; content: string; replyToCommentId: number; replyTo: string; expTag: string }) {
        await this.addNewCommentMutation.mutate({
            photoId,
            photoAuthorId,
            content,
            replyToCommentId,
            replyTo,
            expTag,
        });
    }

    get addCommentResult() {
        return this.addNewCommentMutation.data.visionAddComment;
    }

    get isBlockedComment() {
        return BLOCKED_CODE.some(code => this.addCommentResult?.result === code);
    }

    @apolloMutation<CommentModel>({
        mutation: gql`
            mutation visionLikeComment($photoId: String, $commentId: ID, $cancel: Int, $expTag: String) {
                visionLikeComment(photoId: $photoId, commentId: $commentId, cancel: $cancel, expTag: $expTag) {
                    result
                }
            }
        `,
        variables({ photoId, commentId, cancel, expTag }) {
            return {
                photoId,
                commentId,
                cancel,
                expTag,
            };
        },
    })
    private likeCommentMutation!: MutationResult<
        { photoId: string; commentId: number; cancel: number; expTag: string },
        {
            visionLikeComment: Mutation['visionLikeComment'];
        }
    >;

    // 视频点赞接口  cancel 0 点赞    1 取消点赞
    async likeComment({
        photoId,
        commentId,
        cancel,
        expTag,
    }: { photoId: string; commentId: number; cancel: number; expTag: string }) {
        await this.likeCommentMutation.mutate({
            photoId,
            commentId,
            cancel,
            expTag,
        });
    }

    get likeCommentResult() {
        return this.likeCommentMutation.data.visionLikeComment?.result || 0;
    }

    get sendContent() {
        return this.commentEditState.commentContent;
    }
    get sendReplyContent() {
        return this.replyCommentEditState.commentContent;
    }
    get sendReplyToCommentId() {
        if (
            this.replyCommentEditState.replyToUserName
        ) {
            return this.replyCommentEditState.replyToCommentId;
        }
        return 0;
    }
    get sendReplyTo() {
        if (
            this.replyCommentEditState.replyToUserName
        ) {
            return this.replyCommentEditState.replyTo;
        }
        return 0;
    }
    get commentList() {
        return this.commentListQuery.data.visionCommentList?.rootComments || [];
    }
    set commentList(val) {
        if (this.commentListQuery.data.visionCommentList) {
            this.commentListQuery.data.visionCommentList.rootComments = val;
        }
    }
    get pcursor() {
        return this.commentListQuery.data.visionCommentList?.pcursor || '';
    }
    get commentCount() {
        return this.commentListQuery.data.visionCommentList?.commentCount || 0;
    }
    set commentCount(val: number) {
        if (this.commentListQuery.data.visionCommentList) {
            this.commentListQuery.data.visionCommentList.commentCount = val;
        }
    }
    handleReply(commentId: string, authorId: string, authorName: string) {
        this.replyCommentEditState.replyToCommentId = commentId;
        this.replyCommentEditState.replyTo = authorId;
        this.replyCommentEditState.replyToUserName = authorName;
        this.replyCommentEditState.commentContent = '';
    }
    cleanComment() {
        this.commentEditState.commentContent = '';
        this.commentEditState.replyToCommentId = '';
        this.commentEditState.replyTo = '';
        this.commentEditState.replyToUserName = '';
    }
    cleanReplyComment() {
        this.replyCommentEditState.commentContent = '';
        this.replyCommentEditState.replyToCommentId = '';
        this.replyCommentEditState.replyTo = '';
        this.replyCommentEditState.replyToUserName = '';
    }
    addFakeComment(resultBody: any) {
        // 乐观更新评论
        if (resultBody.replyTo === 0) {
            // 直接评论
            resultBody.subComments = [];
            // TODO乐观更新后面需要改一下，不能用这种策略来……
            // 这个是bug，有坑
            this.commentList!.unshift(resultBody);
        } else {
            const comm = this.commentList!.find(curComm => {
                return curComm!.commentId === resultBody.replyToCommentId
                    || curComm!.subComments!.some(c => c!.commentId === resultBody.replyToCommentId);
            });
            if (comm) {
                comm.subComments!.unshift(resultBody);
            }
        }
    }
    updateCommentResult(res: VisionAddCommentResult | null | undefined, fakeId: string) {
        // 请求返回后更新评论的id ts等信息
        const flattenComments = this.commentList!.reduce((allComments, curValue) => {
            if (curValue?.subComments?.length) {
                return [...allComments, ...curValue.subComments.filter(e => e), curValue];
            }
            return [...allComments, curValue];
        }, [] as Array<VisionRootCommentItem | VisionSubCommentItem | null>);
        const currentCommit = flattenComments.find((val: any) => val.commentId === fakeId);
        if (currentCommit) {
            currentCommit.commentId = res?.commentId || fakeId;
            currentCommit.status = res?.status;
            setTimeout(() => {
                if (!res) {
                    currentCommit.status = this.comment.status.fail;
                    return;
                }
                if (currentCommit.timestamp) {
                    currentCommit.status = this.comment.status.done;
                }
                this.addNewCommentMutation.data.visionAddComment = {};
            }, 3000);
        }
    }
    async addComment(param: any, userInfo: any, isReplyInput?: boolean) {
        const fakeId = nanoid();
        // 先发送评论，判定评论是否要被展示
        await this.sendComment(param, fakeId);
        if (this.isBlockedComment) {
            return;
        }
        const resultBody = {
            authorId: userInfo.eid,
            authorName: userInfo.name,
            commentId: fakeId,
            content: param.content,
            headurl: userInfo.avatar,
            timestamp: +new Date(),
            status: this.comment.status.sending,
            likedCount: 0,
            replyTo: isReplyInput ? this.sendReplyTo : 0,
        };
        if (isReplyInput) {
            Object.assign(resultBody, {
                replyToUserName: this.replyCommentEditState.replyToUserName,
                replyToCommentId: this.replyCommentEditState.replyToCommentId,
            });
        }
        this.addFakeComment(resultBody);
        if (isReplyInput) {
            this.replyInputId = '';
            this.cleanReplyComment();
        }
        this.cleanComment();
    }
    async sendComment(param: any, fakeId: string) {
        if (this.sendReplyTo) {
            Object.assign(param, {
                replyTo: this.sendReplyTo,
            });
        }
        await this.addNewComment(param);
        const res = this.addCommentResult;
        if (res?.result === 1 && typeof this.commentCount === 'number') {
            this.commentCount = this.commentCount + 1;
        }
        // 更新评论池
        setTimeout(() => {
            this.updateCommentResult(this.addCommentResult, fakeId);
        }, 1000);
    }
    // append 是否进行追加 分页时需要
    async getCommentList({
        photoId,
        append = false,
    }: {
        photoId: string;
        append?: boolean;
    }) {
        // 节流
        if (this.pcursor === this.comment.fetchCursorValue && append) {
            return;
        }
        this.comment.fetchCursorValue = this.pcursor!;
        this.commentListQuery.fetchMore({
            variables: {
                pcursor: append ? this.pcursor : '',
                photoId,
            },
            updateQuery: (prev, next) => {
                return {
                    visionCommentList: {
                        __typename: prev.visionCommentList?.__typename,
                        pcursor: next?.visionCommentList?.pcursor,
                        commentCount: next?.visionCommentList?.commentCount,
                        rootComments: append
                            ? [
                                ...prev.visionCommentList?.rootComments || [],
                                ...next?.visionCommentList?.rootComments || [],
                            ]
                            : next?.visionCommentList?.rootComments,
                    },
                };
            },
        });
    }
    async getSubCommentList({
        photoId,
        rootCommentId,
        pcursor,
    }: {
        photoId: string;
        rootCommentId: string;
        pcursor: string;
    }) {
        await this.visionSubCommentListMutation.mutate({
            photoId,
            rootCommentId,
            pcursor,
        });
        const currentRootComment = this.commentList?.find(c => c!.commentId === rootCommentId);
        if (currentRootComment) {
            currentRootComment.subCommentsPcursor = this.subCommentList?.pcursor;
            currentRootComment.subComments!.push(...this.subCommentList?.subComments || []);
        }
        return currentRootComment;
    }
}
